import React, { useState, useEffect } from 'react';
import { Auth0Provider } from '@auth0/auth0-react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import { ErrorBoundary } from './components/shared/ErrorBoundary';
import { AuthForm } from './components/AuthForm';
import { Onboarding } from './components/Onboarding';
import { Layout } from './components/Layout';
import { ContractorDashboard } from './components/contractor/ContractorDashboard';
import { JobMarketplace } from './components/contractor/JobMarketplace';
import { ProfileView } from './components/contractor/ProfileView';
import { InsuranceHub } from './components/contractor/InsuranceHub';
import { ApplicationsView } from './components/contractor/ApplicationsView';
import { GCDashboard } from './components/gc/GCDashboard';
import { JobManagement } from './components/gc/JobManagement';
import { TradesDirectory } from './components/gc/TradesDirectory';
import { ComplianceCenter } from './components/gc/ComplianceCenter';
import { MessagingView } from './components/shared/MessagingView';
import { SettingsView } from './components/shared/SettingsView';
import { HelpView } from './components/shared/HelpView';
import { Marketplace } from './components/shared/Marketplace';
import { PublicProfileView } from './components/shared/PublicProfileView';

function AppContent() {
  const { user, profile, loading, refreshProfile } = useAuth();
  const [currentView, setCurrentView] = useState('dashboard');
  const [publicProfileSlug, setPublicProfileSlug] = useState<string | null>(null);

  useEffect(() => {
    const path = window.location.pathname;
    const profileMatch = path.match(/^\/profile\/([^/]+)$/);
    if (profileMatch) {
      setPublicProfileSlug(profileMatch[1]);
    }
  }, []);

  if (publicProfileSlug) {
    return <PublicProfileView slug={publicProfileSlug} />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (!user) {
    return <AuthForm />;
  }

  if (profile && !profile.onboardingCompleted) {
    return (
      <Onboarding
        userId={user.id}
        onComplete={refreshProfile}
      />
    );
  }

  const renderView = () => {
    const isContractor = profile?.role === 'contractor';

    if (currentView === 'messages') {
      return <MessagingView />;
    }

    if (currentView === 'settings') {
      return <SettingsView />;
    }

    if (currentView === 'help') {
      return <HelpView />;
    }

    if (currentView === 'marketplace') {
      return <Marketplace />;
    }

    if (isContractor) {
      switch (currentView) {
        case 'dashboard':
          return <ContractorDashboard onViewChange={setCurrentView} />;
        case 'profile':
          return <ProfileView />;
        case 'jobs':
          return <JobMarketplace />;
        case 'applications':
          return <ApplicationsView />;
        case 'insurance':
          return <InsuranceHub />;
        default:
          return <ContractorDashboard />;
      }
    } else {
      switch (currentView) {
        case 'dashboard':
          return <GCDashboard onViewChange={setCurrentView} />;
        case 'manage-jobs':
          return <JobManagement />;
        case 'trades':
          return <TradesDirectory />;
        case 'compliance':
          return <ComplianceCenter />;
        default:
          return <GCDashboard />;
      }
    }
  };

  return (
    <Layout currentView={currentView} onViewChange={setCurrentView}>
      {renderView()}
    </Layout>
  );
}

function App() {
  const domain = import.meta.env.VITE_AUTH0_DOMAIN || 'dev-ozzl3buxs6xil1us.us.auth0.com';
  const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID || '7IRgpKS9Wmux8FswWV8o8gElzybIpZHy';
  const redirectUri = window.location.origin;

  return (
    <ErrorBoundary>
      <Auth0Provider
        domain={domain}
        clientId={clientId}
        authorizationParams={{
          redirect_uri: redirectUri,
          audience: `https://${domain}/api/v2/`,
        }}
        useRefreshTokens={true}
        cacheLocation="localstorage"
      >
        <AuthProvider>
          <ToastProvider>
            <AppContent />
          </ToastProvider>
        </AuthProvider>
      </Auth0Provider>
    </ErrorBoundary>
  );
}

export default App;
